import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subscription } from 'rxjs';
import { environment } from '../../../environments/environment';
import { WebsocketService } from '../websocket/websocket.service';
import { ChatSoundService } from '../chat/chat-sound.service';


@Injectable({
  providedIn: 'root'
})
export class NotificationService {

  env = environment;

  /** Back-compat handle: layout components set .onopen/.onmessage on this. */
  ws: any = null;

  private sub: Subscription = null;

  constructor(
    private http: HttpClient,
    public realtime: WebsocketService,
    private chatSound: ChatSoundService,
  ) { }


  getNotification(loginId: number | string): Observable<any> {
    return this.http.get(`${this.env.url}/getNotification/` + loginId);
  }


  notificationRead(notification_id: number | string): Observable<any> {
    return this.http.get(`${this.env.url}/notificationRead/` + notification_id);
  }


  /**
   * Open (or reuse) the shared socket and register this user.
   * Call once per logged-in session, e.g. from the layout component.
   * Old layout code assigned this.notification.ws.onopen/onmessage directly;
   * those assignments are now captured and forwarded to the shared socket.
   */
  connect(user: any): void {
    const id = user && (user.userId || user.user_id || user.iloginId);
    this.realtime.connect({ userId: id });
    if (!this.ws) {
      this.ws = this.makeCompatHandle();
    }
    // Defensive: if the dev-server is serving a stale websocket bundle
    // (class without these streams), skip wiring instead of throwing
    // an unhandled rejection that breaks navigation.
    const notifStream: any = this.realtime && (this.realtime as any).notifications$;
    const statusStream: any = this.realtime && (this.realtime as any).connected$;
    if ((!this.sub || this.sub.closed) && notifStream && typeof notifStream.subscribe === 'function') {
      this.sub = notifStream.subscribe((msg) => {
        if (this.ws && typeof this.ws.onmessage === 'function') {
          try { this.ws.onmessage({ data: JSON.stringify(msg) }); } catch (e) { }
        }
      });
      if (statusStream && typeof statusStream.subscribe === 'function') {
        statusStream.subscribe((open) => {
          if (open && this.ws && typeof this.ws.onopen === 'function') {
            try { this.ws.onopen(); } catch (e) { }
          }
          if (!open && this.ws && typeof this.ws.onclose === 'function') {
            try { this.ws.onclose(); } catch (e) { }
          }
        });
      }
    }
  }

  /** Close the shared socket and clear subscriptions. */
  disconnect(): void {
    if (this.sub && !this.sub.closed) {
      this.sub.unsubscribe();
      this.sub = null;
    }
    this.realtime.disconnect();
  }

  /**
   * Relay a notification frame to online sockets via the shared connection.
   * Accepts either a fan-out hint ({ broadcast: 1 } | { loginId } | { userType })
   * or a backend-built tbl_notification row (has loginId/broadcast/userType plus
   * title/description) — both are normalized to the MessageHandler protocol:
   * { type: 'message', api: 'notification', broadcast?, loginId?, userType?, ... }.
   */
  notify(data: any): void {
    if (!data) { return; }
    const payload: any = { type: 'message', api: 'notification' };
    const pick = ['broadcast', 'loginId', 'userType', 'title', 'description', 'notification_id', 'datetime'];
    for (const k of pick) {
      if (data[k] !== undefined) { payload[k] = data[k]; }
    }
    // loginId of 0/'' (e.g. admin-targeted rows) must still be delivered:
    // MessageHandler treats falsy loginId as broadcast-to-type/all fallback.
    if (payload.loginId === undefined && payload.broadcast === undefined && payload.userType === undefined) {
      payload.broadcast = 1;
    }
    const sent = this.realtime.send(payload);
    if (!sent) {
      console.warn('[NOTIF] notify dropped, socket not open:', payload);
    }
  }

  playSound(): void {
    this.chatSound.play();
  }

  private makeCompatHandle(): any {
    const self = this;
    const handle: any = {
      _onopen: null,
      _onmessage: null,
      _onclose: null,
      _onerror: null,
      send: function (raw: string) {
        try {
          const svc: any = self.realtime;
          if (svc && typeof svc.send === 'function') {
            try { svc.send(JSON.parse(raw)); } catch (e) { svc.send(raw); }
          }
        } catch (e) { }
      },
      close: function () { },
      get readyState() {
        try {
          const svc: any = self.realtime;
          if (svc && typeof svc.isOpen === 'function') {
            return svc.isOpen() ? WebSocket.OPEN : WebSocket.CLOSED;
          }
        } catch (e) { }
        return WebSocket.CONNECTING;
      },
    };
    ['onopen', 'onmessage', 'onclose', 'onerror'].forEach((prop) => {
      Object.defineProperty(handle, prop, {
        get: function () { return handle['_' + prop]; },
        set: function (fn) {
          handle['_' + prop] = fn;
          // If socket already open and user assigns onopen late, fire it immediately.
          // Guarded: old cached bundles may inject a realtime service without isOpen().
          try {
            const svc: any = self.realtime;
            if (prop === 'onopen' && typeof fn === 'function' &&
                svc && typeof svc.isOpen === 'function' && svc.isOpen()) {
              try { fn(); } catch (e) { }
            }
          } catch (e) { }
        },
        enumerable: true,
        configurable: true,
      });
    });
    return handle;
  }
}
